'use client';

import { useRef } from 'react';
import {
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
} from 'framer-motion';

import { cn } from '@/lib/utils';

interface SectionDividerProps {
  /** Direction of the visual transition */
  variant?: 'dark-to-light' | 'light-to-dark';
  /** Hex color for the halftone bars. Defaults to the foreground token. */
  color?: string;
  /**
   * Drop the two lead-in hairlines and re-anchor the dissolve (dark→light only)
   * — used by the hero's closing dissolve, where those hairlines sit under the
   * marquee in the design.
   */
  trimLeadIn?: boolean;
  /** Additional CSS classes */
  className?: string;
}

interface BarConfig {
  /** Vertical position as percentage (0-100) from the design */
  top: number;
  /** Height as percentage of container */
  height: number;
}

/**
 * Halftone dissolve ramp. The original design transcription
 * was authored for a 453px frame — at the compact 60–140px dividers its
 * hairlines rendered sub-pixel and the mid-heavy band read as random
 * stripes, with a dark gap after the final hairline that de-merged the
 * dissolve from the incoming section. This ramp is monotonic instead:
 * thin bars at the outgoing edge growing steadily, the last bar ending
 * flush at 100% so the dissolve merges into the next section's surface.
 * Minimum bar height 1% keeps every bar >=1px at 100px+ frames.
 */
const BARS: BarConfig[] = [
  { top: 8, height: 1 },
  { top: 16, height: 1.8 },
  { top: 25, height: 2.8 },
  { top: 35, height: 4 },
  { top: 46, height: 5.5 },
  { top: 58, height: 7.5 },
  { top: 71, height: 10 },
  { top: 85, height: 15 },
];

/**
 * Light→dark uses the same ramp: dark bars on the light wrapper surface,
 * thin at the top and thickening until the last bar merges flush into the
 * dark section below. Direction is expressed by bar color + wrapper, not
 * by a separate geometry.
 */
const LIGHT_TO_DARK_BARS: BarConfig[] = BARS;

/**
 * Drop the two lead-in hairlines (top 0% / 6.84%) and re-anchor the remaining
 * eight bars to refill 0–100%. In the design those hairlines sit *under* the
 * marquee, so the hero's closing dissolve opens straight on the dense band
 * (this is the pre-1:1 layout). Used via the `trimLeadIn` prop.
 */
const HIDDEN_LEAD_BARS = 2;
function dropLeadInBars(bars: BarConfig[]): BarConfig[] {
  const kept = bars.slice(HIDDEN_LEAD_BARS);
  const offset = kept[0]!.top;
  const span = (100 - offset) / 100;
  return kept.map((b) => ({
    top: (b.top - offset) / span,
    height: b.height / span,
  }));
}

interface ScrollBarStyle {
  /** `calc()` interpolating compressed → spread `top` via the `--s` custom property */
  topCalc: string;
  height: string;
}

/**
 * Scroll-driven bar styles. Every bar starts compressed into one solid block
 * anchored to the BOTTOM of the container (each design set's last bar ends
 * flush at 100%, so the block reads as part of the next section's surface)
 * and fans upward into its exact design `top` as `--s` goes 0 → 1. A single
 * CSS custom property + calc() keeps the interpolation on the compositor —
 * no React re-renders, no per-bar JS per frame.
 */
function computeScrollBarStyles(bars: BarConfig[]): ScrollBarStyle[] {
  const totalHeight = bars.reduce((sum, b) => sum + b.height, 0);
  let y = 100 - totalHeight;
  return bars.map((bar) => {
    const compressed = y;
    y += bar.height;
    const delta = bar.top - compressed;
    return {
      topCalc: `calc(${compressed}% + ${delta}% * var(--s))`,
      height: `${bar.height}%`,
    };
  });
}

// Dark→light is an exact 1:1 of the "Default White" design — all ten
// white bars at their raw spread positions, including the
// two lead-in hairlines at the top. No trim, no rescale.
const BAR_STYLES_DARK_TO_LIGHT = computeScrollBarStyles(BARS);
const BAR_STYLES_LIGHT_TO_DARK = computeScrollBarStyles(LIGHT_TO_DARK_BARS);
const BAR_STYLES_DARK_TO_LIGHT_TRIMMED = computeScrollBarStyles(
  dropLeadInBars(BARS),
);

/**
 * Scroll-driven halftone section divider. The #f2f2f2 (or #121212) bars start
 * as one solid block merged into the bottom edge and fan apart vertically as
 * the divider scrolls through the viewport, settling at their exact design
 * positions — dense at the edge that meets the dark section, thinning to
 * hairlines at the light edge — so one section's background dissolves into
 * the next. Reduced-motion renders the final spread statically.
 */
export function SectionDivider({
  variant = 'dark-to-light',
  color,
  trimLeadIn = false,
  className,
}: SectionDividerProps): React.JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  // light→dark paints DARK bars on the light wrapper surface, leaving
  // transparent gaps that reveal it; dark→light paints light bars.
  const defaultColor = variant === 'light-to-dark' ? '#121212' : '#f2f2f2';

  const reduce = useReducedMotion() ?? false;

  const trimmed = trimLeadIn && variant === 'dark-to-light';

  // Both variants are exact 1:1 copies of their "Перехід" design; the
  // hero closing dissolve trims the two lead-in hairlines (they sit under the
  // marquee in the design).
  const barStyles =
    variant === 'light-to-dark'
      ? BAR_STYLES_LIGHT_TO_DARK
      : trimmed
        ? BAR_STYLES_DARK_TO_LIGHT_TRIMMED
        : BAR_STYLES_DARK_TO_LIGHT;

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  // Fully spread by the time the divider crosses the viewport midpoint, then
  // hold — scrolling back up re-compresses (the effect is scroll-linked, not
  // a one-shot reveal).
  // Bars enter already 40% spread and finish fanning by 35% of the scroll
  // pass — the divider never presents as a solid two-tone slab in the lower
  // half of the viewport.
  const spreadProgress = useTransform(scrollYProgress, [0, 0.35, 1], [0.4, 1, 1]);

  // Write the progress straight to the `--s` custom property — the calc()
  // in each bar's `top` does the rest, off the React render path.
  useMotionValueEvent(spreadProgress, 'change', (v) => {
    if (!reduce) {
      ref.current?.style.setProperty(
        '--s',
        String(Math.min(Math.max(v, 0), 1)),
      );
    }
  });

  return (
    <div
      ref={ref}
      data-variant={variant}
      className={cn(
        'relative w-full contain-layout overflow-hidden',
        // Whitespace compression: the design "Перехід" frame is 241/453px, but
        // the percentage-based bars scale down cleanly, so the divider runs at
        // a fraction of that. `trimLeadIn` now only swaps the bar set — the
        // compressed frame heights are shared by both layouts.
        'h-[60px] md:h-[90px] lg:h-[120px] xl:h-[140px]',
        // Dark→light dissolves the dark hero glow into the next section; nudge
        // it 32px down so the dense band clears the content above it.
        className,
      )}
      style={
        {
          '--s': reduce ? 1 : 0,
          '--divider-color': color ?? defaultColor,
        } as React.CSSProperties
      }
      aria-hidden="true"
    >
      {barStyles.map((bar, index) => (
        <div
          key={index}
          className="absolute left-0 right-0 bg-[var(--divider-color)]"
          style={{ top: bar.topCalc, height: bar.height }}
        />
      ))}
    </div>
  );
}
